import { useMemo } from 'react';

import { useApp } from 'app/context';

/**
 * In-memory store shared across the SPA session for server-paginated lookup
 * tables. Each entry keeps a snapshot of a list page's view state — its filters,
 * search term, sort and pagination — but NOT the fetched rows. Unmounting and
 * remounting the page (e.g. navigating away and back) restores the previous
 * filters and then issues a fresh API call with them, so the user returns to the
 * same view with up-to-date data rather than a stale row snapshot.
 *
 * The store lives in module scope, so it is naturally cleared on a full page
 * reload. It is also scoped by the active partner/mode: whenever that scope
 * changes, every cached view is dropped so a partner switch never restores
 * another partner's filters.
 */
const listCaches = new Map<string, unknown>();

let activeScope: string | undefined;

const ensureScope = (scope: string) => {

    if (activeScope !== scope) {

        listCaches.clear();
        activeScope = scope;

    }

};

export type ListCache<T> = {
    clear: () => void;
    read: () => T | undefined;
    write: (value: T) => void;
};

/**
 * Provides read/write access to the shared lookup-table view store for a given
 * namespace (typically the page name). Persist only view state (filters, search,
 * sort, pagination) here — never the fetched rows. The returned helpers are
 * stable for the lifetime of the current partner/mode scope.
 *
 * @param namespace - Unique identifier for the list (e.g. `'groups'`).
 */
export const useListCache = <T>(namespace: string): ListCache<T> => {

    const { isSystemMode, selectedPartner } = useApp();

    const scope = `${isSystemMode ? 'system' : 'partner'}:${selectedPartner?.id ?? 'none'}`;

    return useMemo(() => {

        const key = `${scope}:${namespace}`;

        return {
            clear: () => {

                listCaches.delete(key);

            },
            read: (): T | undefined => {

                ensureScope(scope);
                return listCaches.get(key) as T | undefined;

            },
            write: (value: T) => {

                ensureScope(scope);
                listCaches.set(key, value);

            }
        };

    }, [namespace, scope]);

};
